import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — 52. BUSINESS PROFILE
// Profil public standardisé d'une entreprise. Il alimente ensuite :
//   Zifek Space · la recherche · le SEO · Zifek AI · les pages publiques.
// Une seule source de vérité, consommée par tous les canaux.
// ─────────────────────────────────────────────────────────────

export interface BusinessHour {
  day: string;
  open?: string;
  close?: string;
  closed?: boolean;
}

export const WEEK_DAYS: { key: string; label: string }[] = [
  { key: 'mon', label: 'Lundi' },
  { key: 'tue', label: 'Mardi' },
  { key: 'wed', label: 'Mercredi' },
  { key: 'thu', label: 'Jeudi' },
  { key: 'fri', label: 'Vendredi' },
  { key: 'sat', label: 'Samedi' },
  { key: 'sun', label: 'Dimanche' },
];

export const SOCIAL_NETWORKS: { key: string; label: string; icon: string; placeholder: string }[] = [
  { key: 'facebook', label: 'Facebook', icon: 'ri-facebook-circle-line', placeholder: 'https://facebook.com/…' },
  { key: 'instagram', label: 'Instagram', icon: 'ri-instagram-line', placeholder: 'https://instagram.com/…' },
  { key: 'tiktok', label: 'TikTok', icon: 'ri-tiktok-line', placeholder: 'https://tiktok.com/@…' },
  { key: 'linkedin', label: 'LinkedIn', icon: 'ri-linkedin-box-line', placeholder: 'https://linkedin.com/company/…' },
  { key: 'youtube', label: 'YouTube', icon: 'ri-youtube-line', placeholder: 'https://youtube.com/@…' },
  { key: 'x', label: 'X (Twitter)', icon: 'ri-twitter-x-line', placeholder: 'https://x.com/…' },
];

export interface CoreBusinessProfile {
  id: number;
  tenant_id: number | null;
  organization_id: number | null;
  name: string;
  slug: string | null;
  logo: string | null;
  cover: string | null;
  description: string | null;
  short_description: string | null;
  category: string | null;
  subcategory: string | null;
  services: unknown[] | null;
  products: unknown[] | null;
  photos: string[] | null;
  videos: string[] | null;
  address: string | null;
  city: string | null;
  country: string | null;
  latitude: number | null;
  longitude: number | null;
  hours: Record<string, BusinessHour> | null;
  phone: string | null;
  whatsapp: string | null;
  email: string | null;
  website: string | null;
  socials: Record<string, string> | null;
  languages: string[] | null;
  rating: number | null;
  review_count: number | null;
  verification_level: string | null;
  is_public: boolean | null;
  seo_title: string | null;
  seo_description: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface BusinessProfileInput {
  organizationId: number;
  tenantId?: number | null;
  name: string;
  slug?: string | null;
  logo?: string | null;
  cover?: string | null;
  description?: string | null;
  shortDescription?: string | null;
  category?: string | null;
  subcategory?: string | null;
  services?: unknown[];
  products?: unknown[];
  photos?: string[];
  videos?: string[];
  address?: string | null;
  city?: string | null;
  country?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  hours?: Record<string, BusinessHour> | null;
  phone?: string | null;
  whatsapp?: string | null;
  email?: string | null;
  website?: string | null;
  socials?: Record<string, string> | null;
  languages?: string[];
  isPublic?: boolean;
  seoTitle?: string | null;
  seoDescription?: string | null;
}

function slugify(value: string, fallback: string): string {
  const base = (value || '').trim().toLowerCase();
  const slug = base.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return slug || fallback;
}

export async function getBusinessProfile(organizationId: number): Promise<CoreBusinessProfile | null> {
  const { data, error } = await supabase
    .from('core_business_profiles')
    .select('*')
    .eq('organization_id', organizationId)
    .maybeSingle();
  if (error) throw error;
  return (data as CoreBusinessProfile) || null;
}

/**
 * Récupère un profil PUBLIC par son slug — alimente la page /business/:slug.
 * Un profil non public (is_public=false) n'est jamais exposé.
 */
export async function getBusinessProfileBySlug(slug: string): Promise<CoreBusinessProfile | null> {
  const clean = (slug || '').trim().toLowerCase();
  if (!clean) return null;
  const { data, error } = await supabase
    .from('core_business_profiles')
    .select('*')
    .eq('slug', clean)
    .eq('is_public', true)
    .maybeSingle();
  if (error) throw error;
  return (data as CoreBusinessProfile) || null;
}

/** Crée ou met à jour le profil public de l'entreprise (une ligne par organisation). */
export async function upsertBusinessProfile(input: BusinessProfileInput): Promise<number | null> {
  const existing = await getBusinessProfile(input.organizationId);
  const payload: Record<string, unknown> = {
    tenant_id: input.tenantId ?? null,
    organization_id: input.organizationId,
    name: input.name,
    slug: slugify(input.slug || input.name, `biz-${input.organizationId}`),
    updated_at: new Date().toISOString(),
  };
  const setIf = (cond: boolean, key: string, value: unknown) => { if (cond) payload[key] = value; };
  setIf(input.logo !== undefined, 'logo', input.logo || null);
  setIf(input.cover !== undefined, 'cover', input.cover || null);
  setIf(input.description !== undefined, 'description', input.description || null);
  setIf(input.shortDescription !== undefined, 'short_description', input.shortDescription || null);
  setIf(input.category !== undefined, 'category', input.category || null);
  setIf(input.subcategory !== undefined, 'subcategory', input.subcategory || null);
  setIf(input.services !== undefined, 'services', input.services || []);
  setIf(input.products !== undefined, 'products', input.products || []);
  setIf(input.photos !== undefined, 'photos', input.photos || []);
  setIf(input.videos !== undefined, 'videos', input.videos || []);
  setIf(input.address !== undefined, 'address', input.address || null);
  setIf(input.city !== undefined, 'city', input.city || null);
  setIf(input.country !== undefined, 'country', input.country || null);
  setIf(input.latitude !== undefined, 'latitude', input.latitude ?? null);
  setIf(input.longitude !== undefined, 'longitude', input.longitude ?? null);
  setIf(input.hours !== undefined, 'hours', input.hours || {});
  setIf(input.phone !== undefined, 'phone', input.phone || null);
  setIf(input.whatsapp !== undefined, 'whatsapp', input.whatsapp || null);
  setIf(input.email !== undefined, 'email', input.email || null);
  setIf(input.website !== undefined, 'website', input.website || null);
  setIf(input.socials !== undefined, 'socials', input.socials || {});
  setIf(input.languages !== undefined, 'languages', input.languages || []);
  setIf(input.isPublic !== undefined, 'is_public', !!input.isPublic);
  setIf(input.seoTitle !== undefined, 'seo_title', input.seoTitle || null);
  setIf(input.seoDescription !== undefined, 'seo_description', input.seoDescription || null);

  if (existing?.id) {
    const { error } = await supabase.from('core_business_profiles').update(payload).eq('id', existing.id);
    if (error) throw error;
    return existing.id;
  }
  const { data, error } = await supabase.from('core_business_profiles').insert(payload).select('id').maybeSingle();
  if (error) throw error;
  return (data?.id as number) ?? null;
}

export async function setBusinessProfilePublic(organizationId: number, isPublic: boolean): Promise<void> {
  const { error } = await supabase
    .from('core_business_profiles')
    .update({ is_public: isPublic, updated_at: new Date().toISOString() })
    .eq('organization_id', organizationId);
  if (error) throw error;
}

/** Profils publics — alimente Zifek Space / les pages publiques / la recherche. */
export async function listPublicBusinessProfiles(filter?: {
  city?: string;
  category?: string;
  search?: string;
  limit?: number;
}): Promise<CoreBusinessProfile[]> {
  let query = supabase
    .from('core_business_profiles')
    .select('*')
    .eq('is_public', true)
    .order('rating', { ascending: false })
    .limit(filter?.limit ?? 60);
  if (filter?.city) query = query.ilike('city', `%${filter.city}%`);
  if (filter?.category) query = query.eq('category', filter.category);
  if (filter?.search) query = query.or(`name.ilike.%${filter.search}%,description.ilike.%${filter.search}%`);
  const { data, error } = await query;
  if (error) throw error;
  return (data || []) as CoreBusinessProfile[];
}

// ── Projections du profil vers les autres canaux ──

export interface BusinessProfileSeo {
  title: string;
  description: string;
  keywords: string[];
}

/** Génère les métadonnées SEO à partir du profil (spec 52 → SEO). */
export function buildProfileSeo(profile: CoreBusinessProfile): BusinessProfileSeo {
  const parts = [profile.name];
  if (profile.category) parts.push(profile.category);
  if (profile.city) parts.push(profile.city);
  const title = (profile.seo_title || parts.join(' — ')).slice(0, 60);
  const description = (profile.seo_description
    || profile.short_description
    || profile.description
    || `Découvrez ${profile.name}${profile.city ? ` à ${profile.city}` : ''}.`
  ).slice(0, 160);
  const keywords = [profile.name, profile.category, profile.city, profile.country, ...(profile.languages || [])]
    .filter(Boolean)
    .map((k) => String(k));
  return { title, description, keywords };
}

/** Résumé compact réutilisable par Zifek AI et la recherche. */
export function buildProfileSummary(profile: CoreBusinessProfile): string {
  const chips = [profile.category, profile.city, profile.phone ? `☎ ${profile.phone}` : null]
    .filter(Boolean)
    .join(' · ');
  const desc = profile.short_description || profile.description || '';
  return `${profile.name}${chips ? ` — ${chips}` : ''}${desc ? `. ${desc}` : ''}`;
}

/** Version publique épurée (uniquement les champs exposables). */
export function toPublicProfile(profile: CoreBusinessProfile) {
  return {
    name: profile.name,
    slug: profile.slug,
    logo: profile.logo,
    cover: profile.cover,
    description: profile.description,
    shortDescription: profile.short_description,
    category: profile.category,
    address: profile.address,
    city: profile.city,
    country: profile.country,
    latitude: profile.latitude,
    longitude: profile.longitude,
    hours: profile.hours,
    phone: profile.phone,
    whatsapp: profile.whatsapp,
    website: profile.website,
    socials: profile.socials,
    photos: profile.photos,
    videos: profile.videos,
    languages: profile.languages,
    rating: profile.rating,
    reviewCount: profile.review_count,
    verificationLevel: profile.verification_level,
    seo: buildProfileSeo(profile),
  };
}

export interface ProfileCompleteness {
  percent: number;
  missing: string[];
}

/** Calcule le taux de complétion du profil (guide l'utilisateur). */
export function profileCompleteness(profile: CoreBusinessProfile | null): ProfileCompleteness {
  if (!profile) {
    return { percent: 0, missing: ['name', 'description', 'logo', 'phone', 'address', 'hours', 'photos'] };
  }
  const checks: { key: string; ok: boolean }[] = [
    { key: 'name', ok: !!profile.name },
    { key: 'description', ok: !!profile.description },
    { key: 'logo', ok: !!profile.logo },
    { key: 'phone', ok: !!profile.phone },
    { key: 'address', ok: !!profile.address },
    { key: 'hours', ok: !!profile.hours && Object.keys(profile.hours || {}).length > 0 },
    { key: 'photos', ok: !!profile.photos && profile.photos.length > 0 },
    { key: 'socials', ok: !!profile.socials && Object.keys(profile.socials || {}).length > 0 },
  ];
  const done = checks.filter((c) => c.ok).length;
  return { percent: Math.round((done / checks.length) * 100), missing: checks.filter((c) => !c.ok).map((c) => c.key) };
}